import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { CalendarDays, Clock, CheckCircle2, Users, ExternalLink, Cpu, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [me, setMe] = useState(null);

    useEffect(() => {
        api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {});
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
        const today = new Date().toISOString().slice(0, 10);
        api.get("/admin/reservations", { params: { date_filter: today } }).then((r) => setRecent(r.data)).catch(() => {});
    }, []);

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-dashboard">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="mb-10 flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">{me?.restaurant?.name || "Pannello"}</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Buongiorno.</h1>
                    </div>
                    {me?.restaurant?.slug && (
                        <a href={`/r/${me.restaurant.slug}`} target="_blank" rel="noreferrer"
                            data-testid="visit-public-link"
                            className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-gold px-4 py-2.5 border border-white/10 rounded-sm">
                            <ExternalLink size={14} /> Vedi pagina pubblica · /r/{me.restaurant.slug}
                        </a>
                    )}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <StatCard icon={CalendarDays} label="Prenotazioni totali" value={stats?.total ?? "—"} />
                    <StatCard icon={Clock} label="Oggi" value={stats?.today ?? "—"} accent />
                    <StatCard icon={CheckCircle2} label="In attesa" value={stats?.pending ?? "—"} />
                    <StatCard icon={Users} label="Tavoli configurati" value={stats?.tables_count ?? "—"} />
                </div>

                {/* SmartTable cross-promo */}
                <a href="/admin/integrations" data-testid="smarttable-banner"
                    className="block bg-gradient-to-br from-[#0f1730] via-[#0a0a0c] to-[#1a1f3a] border border-gold/30 rounded-sm p-6 mb-10 hover:border-gold/60 hover:shadow-[0_0_30px_rgba(212,175,55,0.15)] transition-all group">
                    <div className="flex items-center gap-5 flex-wrap">
                        <div className="w-14 h-14 rounded-sm bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
                            <Cpu size={26} className="text-gold" strokeWidth={1.4} />
                        </div>
                        <div className="flex-1 min-w-[260px]">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-gold/80 font-semibold">Novità</div>
                            <h3 className="font-serif text-2xl text-white mt-0.5">Scarica anche la nostra <span className="italic text-gold">Renova gestione tavoli</span></h3>
                            <p className="text-sm text-gray-400 mt-1.5"><strong className="text-white">SmartTable</strong> by Renova360 — sincronizza in tempo reale le prenotazioni di Prenova con la tua gestione tavoli, comande e POS.</p>
                        </div>
                        <div className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase font-semibold group-hover:translate-x-1 transition-transform">
                            Configura API <ArrowRight size={14} strokeWidth={2} />
                        </div>
                    </div>
                </a>

                <div className="bg-ink-surface border border-white/10 rounded-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-serif text-2xl text-white">Prenotazioni di oggi</h2>
                        <a href="/admin/reservations" className="text-xs text-gold tracking-widest uppercase hover:text-gold-light">Vedi tutte →</a>
                    </div>
                    {recent.length === 0 ? (
                        <p className="text-gray-500 text-sm py-8 text-center">Nessuna prenotazione per oggi.</p>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {recent.map((r) => (
                                <div key={r.reservation_id} className="py-4 flex items-center justify-between">
                                    <div>
                                        <div className="text-white font-medium">{r.customer_name}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {r.slot_time} · {r.table_number ? `Tavolo ${r.table_number}` : "Tavolo da assegnare"} · {r.party_size} coperti
                                        </div>
                                    </div>
                                    <StatusPill status={r.status} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent }) {
    return (
        <div className={`p-6 rounded-sm border ${accent ? "border-gold/40 bg-gold/5" : "border-white/10 bg-ink-surface"}`}>
            <Icon size={20} className={accent ? "text-gold" : "text-gray-500"} strokeWidth={1.5} />
            <div className="text-xs text-gray-500 tracking-widest uppercase mt-4">{label}</div>
            <div className="font-serif text-4xl text-white mt-1">{value}</div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
        confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
        cancelled: "bg-red-500/10 text-red-400 border-red-500/30",
    };
    const label = { pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata" }[status];
    return <span className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 rounded-sm ${map[status]}`}>{label}</span>;
}
